import { css } from 'styled-components';

import { defaultTheme as gstarTheme } from '~/core/theme/g-star';
import { SiteType } from '~/utilities/graphql/codegen';
import { getSiteStyles } from '~/utilities/helpers';

/**
 * Non-button colours read the G-Star palette on every site, so WAHTS keeps its
 * previous rendering until those values have a WAHTS design check. Outline and
 * solid button colours use the theme (verified against the WAHTS Figma).
 */
export const gstarColors = gstarTheme.colors;

/**
 * AAF button palette on dark. Also AAF's default: applied to every .contentBlock
 * and to ColorScheme.initial (the --smDark/--mdDark reset), so AAF buttons never
 * fall back to the G-Star greys in the base var() fallbacks.
 */
export const aafDarkButtons = css`
  ${({ theme }) =>
    getSiteStyles(theme.siteType, {
      [SiteType.Aaf]: css`
        --contentBlock-button-border-color: ${theme.colors.neutral['900']};
        --contentBlock-button-text-color: ${theme.colors.neutral['900']};
        --contentBlock-button-hover-border-color: ${theme.colors.neutral['900']};
        --contentBlock-button-hover-background-color: ${theme.colors.neutral['900']};
        --contentBlock-button-hover-text-color: ${theme.colors.neutral['0']};
        --contentBlock-button-active-border-color: ${theme.colors.neutral['900']};
        --contentBlock-button-active-background-color: ${theme.colors.neutral['900']};
        --contentBlock-button-active-text-color: ${theme.colors.neutral['0']};

        /* Solid */
        --contentBlock-buttonSolid-border-color: ${theme.colors.neutral['900']};
        --contentBlock-buttonSolid-background-color: ${theme.colors.neutral['900']};
        --contentBlock-buttonSolid-text-color: ${theme.colors.neutral['0']};
        --contentBlock-buttonSolid-hover-border-color: ${theme.colors.neutral['900']};
        --contentBlock-buttonSolid-hover-background-color: transparent;
        --contentBlock-buttonSolid-hover-text-color: ${theme.colors.neutral['900']};
        --contentBlock-buttonSolid-active-border-color: ${theme.colors.neutral['900']};
        --contentBlock-buttonSolid-active-background-color: ${theme.colors.neutral['900']};
        --contentBlock-buttonSolid-active-text-color: ${theme.colors.neutral['0']};

        /* Link & Underline */
        --contentBlock-buttonText-color: ${theme.colors.neutral['900']};
        --contentBlock-buttonText-hover-color: ${theme.colors.neutral['900']};
        --contentBlock-buttonText-active-color: ${theme.colors.neutral['900']};
      `,
    })}
`;

export const ColorScheme = {
  initial: css`
    --contentBlock-text-color: initial;
    --contentBlock-shadow-color: initial;
    --contentBlock-shadow-opacity: initial;
    --contentBlock-background-color: initial;

    /* Button Outline */
    --contentBlock-button-border-color: initial;
    --contentBlock-button-background-color: initial;
    --contentBlock-button-text-color: initial;
    --contentBlock-button-hover-border-color: initial;
    --contentBlock-button-hover-background-color: initial;
    --contentBlock-button-hover-text-color: initial;
    --contentBlock-button-active-border-color: initial;
    --contentBlock-button-active-background-color: initial;
    --contentBlock-button-active-text-color: initial;

    /* Button Solid */
    --contentBlock-buttonSolid-border-color: initial;
    --contentBlock-buttonSolid-background-color: initial;
    --contentBlock-buttonSolid-text-color: initial;
    --contentBlock-buttonSolid-hover-border-color: initial;
    --contentBlock-buttonSolid-hover-background-color: initial;
    --contentBlock-buttonSolid-hover-text-color: initial;
    --contentBlock-buttonSolid-active-border-color: initial;
    --contentBlock-buttonSolid-active-background-color: initial;
    --contentBlock-buttonSolid-active-text-color: initial;

    /* Button Link & Underline */
    --contentBlock-buttonText-color: initial;
    --contentBlock-buttonText-hover-color: initial;
    --contentBlock-buttonText-active-color: initial;

    /* Category Tag */
    --categoryTag-border-color: initial;
    --categoryTag-background-color: initial;
    --categoryTag-text-color: initial;
    --categoryTag-hover-border-color: initial;
    --categoryTag-hover-background-color: initial;
    --categoryTag-hover-text-color: initial;
    --categoryTag-active-border-color: initial;
    --categoryTag-active-background-color: initial;
    --categoryTag-active-text-color: initial;
    --categoryTag-is-active-border-color: initial;
    --categoryTag-is-active-background-color: initial;
    --categoryTag-is-active-text-color: initial;

    ${aafDarkButtons}
  `,
  dark: css`
    ${({ theme }) => css`
      --contentBlock-text-color: ${gstarColors.slate['900']};
      --contentBlock-shadow-opacity: 0;
      --contentBlock-background-color: ${gstarColors.neutral['0']};

      /* Button Outline */
      --contentBlock-button-border-color: ${theme.colors.slate['900']};
      --contentBlock-button-background-color: transparent;
      --contentBlock-button-text-color: ${theme.colors.slate['900']};
      --contentBlock-button-hover-border-color: ${theme.colors.neutral['800']};
      --contentBlock-button-hover-background-color: ${theme.colors.neutral['800']};
      --contentBlock-button-hover-text-color: ${theme.colors.neutral['0']};
      --contentBlock-button-active-border-color: ${theme.colors.slate['900']};
      --contentBlock-button-active-background-color: ${theme.colors.slate['600']};
      --contentBlock-button-active-text-color: ${theme.colors.neutral['0']};

      /* Button Solid */
      --contentBlock-buttonSolid-border-color: ${theme.colors.neutral['800']};
      --contentBlock-buttonSolid-background-color: ${theme.colors.neutral['800']};
      --contentBlock-buttonSolid-text-color: ${theme.colors.neutral['0']};
      --contentBlock-buttonSolid-hover-border-color: ${theme.colors.slate['600']};
      --contentBlock-buttonSolid-hover-background-color: ${theme.colors.slate['600']};
      --contentBlock-buttonSolid-hover-text-color: ${theme.colors.neutral['0']};
      --contentBlock-buttonSolid-active-border-color: ${theme.colors.slate['900']};
      --contentBlock-buttonSolid-active-background-color: ${theme.colors.slate['900']};
      --contentBlock-buttonSolid-active-text-color: ${theme.colors.neutral['0']};

      /* Button Link & Underline */
      --contentBlock-buttonText-color: ${gstarColors.slate['900']};
      --contentBlock-buttonText-hover-color: ${gstarColors.slate['600']};
      --contentBlock-buttonText-active-color: ${gstarColors.slate['900']};

      /* Category Tag */
      --categoryTag-border-color: ${gstarColors.slate['900']};
      --categoryTag-background-color: transparent;
      --categoryTag-text-color: ${gstarColors.slate['900']};
      --categoryTag-hover-border-color: ${gstarColors.slate['900']};
      --categoryTag-hover-background-color: #21252911;
      --categoryTag-hover-text-color: ${gstarColors.slate['900']};
      --categoryTag-active-border-color: ${gstarColors.slate['900']};
      --categoryTag-active-background-color: #21252911;
      --categoryTag-active-text-color: ${gstarColors.slate['900']};
      --categoryTag-is-active-border-color: initial;
      --categoryTag-is-active-background-color: initial;
      --categoryTag-is-active-text-color: initial;

      ${aafDarkButtons}
    `}
  `,
  light: css`
    ${({ theme }) => css`
      --contentBlock-text-color: ${gstarColors.neutral['0']};
      --contentBlock-shadow-opacity: 0.3;
      --contentBlock-background-color: ${gstarColors.neutral['500']};

      /* Button Outline */
      --contentBlock-button-border-color: ${theme.colors.neutral['0']};
      --contentBlock-button-background-color: transparent;
      --contentBlock-button-text-color: ${theme.colors.neutral['0']};
      --contentBlock-button-hover-border-color: ${theme.colors.neutral['0']};
      --contentBlock-button-hover-background-color: ${theme.colors.neutral['0']};
      --contentBlock-button-hover-text-color: ${theme.colors.slate['900']};
      --contentBlock-button-active-border-color: ${theme.colors.slate['300']};
      --contentBlock-button-active-background-color: ${theme.colors.slate['300']};
      --contentBlock-button-active-text-color: ${theme.colors.slate['900']};

      /* Button Solid */
      --contentBlock-buttonSolid-border-color: ${theme.colors.neutral['0']};
      --contentBlock-buttonSolid-background-color: ${theme.colors.neutral['0']};
      --contentBlock-buttonSolid-text-color: ${theme.colors.slate['900']};
      --contentBlock-buttonSolid-hover-border-color: ${theme.colors.neutral['200']};
      --contentBlock-buttonSolid-hover-background-color: ${theme.colors.neutral['200']};
      --contentBlock-buttonSolid-hover-text-color: ${theme.colors.slate['900']};
      --contentBlock-buttonSolid-active-border-color: ${theme.colors.slate['300']};
      --contentBlock-buttonSolid-active-background-color: ${theme.colors.slate['300']};
      --contentBlock-buttonSolid-active-text-color: ${theme.colors.slate['900']};

      /* Button Link & Underline */
      --contentBlock-buttonText-color: ${gstarColors.neutral['0']};
      --contentBlock-buttonText-hover-color: ${gstarColors.neutral['300']};
      --contentBlock-buttonText-active-color: ${gstarColors.neutral['0']};

      /* Category Tag */
      --categoryTag-border-color: ${gstarColors.neutral['0']};
      --categoryTag-background-color: transparent;
      --categoryTag-text-color: ${gstarColors.neutral['0']};
      --categoryTag-hover-border-color: ${gstarColors.neutral['0']};
      --categoryTag-hover-background-color: #fff1;
      --categoryTag-hover-text-color: ${gstarColors.neutral['0']};
      --categoryTag-active-border-color: ${gstarColors.neutral['0']};
      --categoryTag-active-background-color: #fff1;
      --categoryTag-active-text-color: ${gstarColors.neutral['0']};
      --categoryTag-is-active-border-color: #fefefe;
      --categoryTag-is-active-background-color: ${gstarColors.neutral['0']};
      --categoryTag-is-active-text-color: ${gstarColors.slate['900']};

      /* AAF button colours */
      ${({ theme }) =>
        getSiteStyles(theme.siteType, {
          [SiteType.Aaf]: css`
            --contentBlock-button-hover-text-color: ${theme.colors.neutral['900']};
            --contentBlock-button-active-border-color: ${theme.colors.neutral['0']};
            --contentBlock-button-active-background-color: ${theme.colors.neutral['0']};
            --contentBlock-button-active-text-color: ${theme.colors.neutral['900']};

            /* Solid */
            --contentBlock-buttonSolid-border-color: ${theme.colors.neutral['0']};
            --contentBlock-buttonSolid-background-color: ${theme.colors.neutral['0']};
            --contentBlock-buttonSolid-text-color: ${theme.colors.neutral['900']};
            --contentBlock-buttonSolid-hover-border-color: ${theme.colors.neutral['0']};
            --contentBlock-buttonSolid-hover-background-color: transparent;
            --contentBlock-buttonSolid-hover-text-color: ${theme.colors.neutral['0']};
            --contentBlock-buttonSolid-active-border-color: ${theme.colors.neutral['0']};
            --contentBlock-buttonSolid-active-background-color: ${theme.colors.neutral['0']};
            --contentBlock-buttonSolid-active-text-color: ${theme.colors.neutral['900']};
          `,
        })}
    `}
  `,
};
